import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { SEGMENT_SPECIFIC_RULES, validateSegmentForm } from "./SegmentData";
import CommonHeadingNBtns from "../../../common-components/CommonHeadingNBtns";
import SelectDropdown from "../../../components-react/SelectDropdown";
import { segment_types } from "../../../common-data/DropdowData";
import DateRanger from "../../../components-react/DateRanger";
import MultiSelect from "../../../components-react/MultiSelect";
import { fetchGymData, fetchTrainersData } from "../../../../data/APIs";
import { fetchInHouseUsersData } from "../../../../redux/reducers/dropdownApiSlice";
import { selectGymData } from "../../../../redux/reducers/gymDataSlice";
import { selectTrainersData } from "../../../../redux/reducers/trainerDataSlice";
import CloseSubmitBtn from "../../../common-components/custom-comps/CloseSubmitBtn";
import { showConfirmAlert } from "../../../components-react/ReactAlert";
import { commonPostPutApi } from "../../../../redux/reducers/commonApiSlice";

const SegementForm = ({
  isEdit,
  rowData,
  closeModal,
  userInfoData,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);

  const gymData = useSelector(selectGymData);
  const trainersData = useSelector(selectTrainersData);
  const { loading: inhouseUsersLoading, inhouseUsers } = useSelector(
    (state) => state.dropdown
  );

  const [isRulesExpanded, setIsRulesExpanded] = useState(false);
  const [activeDateRanges, setActiveDateRanges] = useState({});
  const [dateRangeStates, setDateRangeStates] = useState({});

  const iniFormValues = {
    segmentName: "",
    selectedSegmentType: "",
    description: "",
    selectedRules: [],
    selectedRuleConditions: {},
    isActive: false,
  };
  const [formValues, setFormValues] = useState(iniFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  // --- EVENT HANDLERS ---
  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;

    setIsSubmit(false);
    setFormValues((prevs) => ({
      ...prevs,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeDropdown = (value, name) => {
    setIsSubmit(false);

    // If segment type is changing, clear selected rules and conditions
    if (name === "selectedSegmentType") {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
        selectedRules: [],
        selectedRuleConditions: {},
      }));

      // Clear date range states when segment type changes
      setActiveDateRanges({});
      setDateRangeStates({});
    } else {
      setFormValues({ ...formValues, [name]: value });
    }
  };

  const handleRuleToggle = (ruleValue) => {
    const rule = availableRules.find((r) => r.value === ruleValue);
    const isSelected = formValues.selectedRules.some(
      (r) => r.value === ruleValue
    );

    if (isSelected) {
      setFormValues((prev) => ({
        ...prev,
        selectedRules: prev.selectedRules.filter((r) => r.value !== ruleValue),
        selectedRuleConditions: (() => {
          const newConditions = { ...prev.selectedRuleConditions };
          delete newConditions[ruleValue];
          return newConditions;
        })(),
      }));

      setActiveDateRanges((prev) => {
        const newStates = { ...prev };
        delete newStates[ruleValue];
        return newStates;
      });
      setDateRangeStates((prev) => {
        const newStates = { ...prev };
        delete newStates[ruleValue];
        return newStates;
      });
    } else {
      setFormValues((prev) => ({
        ...prev,
        selectedRules: [...prev.selectedRules, rule],
      }));
    }
  };

  const handleSelectAllRules = () => {
    const allRulesSelected =
      availableRules.length > 0 &&
      formValues.selectedRules.length === availableRules.length;

    if (allRulesSelected) {
      setFormValues((prev) => ({
        ...prev,
        selectedRules: [],
        selectedRuleConditions: {},
      }));
      setActiveDateRanges({});
      setDateRangeStates({});
    } else {
      setFormValues((prev) => ({
        ...prev,
        selectedRules: [...availableRules],
      }));
    }
  };

  const handleConditionChange = (ruleValue, value) => {
    setFormValues((prev) => ({
      ...prev,
      selectedRuleConditions: {
        ...prev.selectedRuleConditions,
        [ruleValue]: value,
      },
    }));
  };

  const availableRules =
    SEGMENT_SPECIFIC_RULES[formValues.selectedSegmentType?.label] || [];

  const areAllRulesSelected =
    availableRules.length > 0 &&
    formValues.selectedRules.length === availableRules.length;

  const getOptionsForRule = (rule) => {
    if (rule.value === "gym_id") {
      return gymData || [];
    } else if (rule.value === "trainer_id") {
      return trainersData || [];
    } else if (rule.value === "added_by") {
      return inhouseUsers || [];
    }
    return rule.options || [];
  };

  // DYNAMIC FIELD RENDERER
  const renderFieldForRule = (rule) => {
    const { value: ruleValue, label, inputType } = rule;
    const options = getOptionsForRule(rule);

    switch (inputType) {
      case "select":
        return (
          <>
            <div
              className={`ris-select ${
                formErrors.selectedSegmentType ? "formError" : ""
              }`}
            >
              <SelectDropdown
                options={options}
                selectedOption={
                  formValues.selectedRuleConditions[ruleValue] || ""
                }
                setSelectedOption={(selectedValue) =>
                  handleConditionChange(ruleValue, selectedValue)
                }
                name={label}
                isDisable={inhouseUsersLoading}
              />
            </div>
          </>
        );
      case "multi-select":
        return (
          <div className="ris-select ris-multi-select">
            <MultiSelect
              options={options}
              state={formValues.selectedRuleConditions[ruleValue] || []}
              setState={(selectedValue) =>
                handleConditionChange(ruleValue, selectedValue)
              }
              name={label}
              preventKeyboardOpen={true}
              isDisable={false}
            />
          </div>
        );
      case "date-range":
        const dateRangeValue = formValues.selectedRuleConditions[ruleValue];

        if (!dateRangeStates[ruleValue]) {
          setDateRangeStates((prev) => ({
            ...prev,
            [ruleValue]: [
              {
                startDate: dateRangeValue?.from
                  ? new Date(dateRangeValue.from)
                  : new Date(),
                endDate: dateRangeValue?.to
                  ? new Date(dateRangeValue.to)
                  : new Date(),
                key: "selection",
              },
            ],
          }));
        }

        return (
          <div className="ris-date-range-container">
            <div className="ris-date-range-input-wrapper">
              <button
                type="button"
                className={`ris-date-range-button ${
                  dateRangeValue?.from || dateRangeValue?.to ? "has-value" : ""
                }`}
                onClick={() =>
                  setActiveDateRanges((prev) => ({
                    ...prev,
                    [ruleValue]: !prev[ruleValue],
                  }))
                }
              >
                {dateRangeValue?.from && dateRangeValue?.to
                  ? `${new Date(
                      dateRangeValue.from
                    ).toLocaleDateString()} - ${new Date(
                      dateRangeValue.to
                    ).toLocaleDateString()}`
                  : `Select ${label}`}
              </button>

              {(dateRangeValue?.from || dateRangeValue?.to) && (
                <button
                  type="button"
                  className="ris-clear-date-range"
                  onClick={() => clearDateRange(ruleValue)}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        );
      case "date":
      case "number":
      case "text":
      case "time":
        return (
          <input
            type={inputType}
            className={`ris-input ${
              "date" === inputType &&
              formValues.selectedRuleConditions[ruleValue]
                ? "date-has-value"
                : ""
            }`}
            value={formValues.selectedRuleConditions[ruleValue] || ""}
            onChange={(e) => handleConditionChange(ruleValue, e.target.value)}
            placeholder={`Enter ${label}`}
          />
        );
      default:
        return null;
    }
  };
  // -------------------- DYNAMIC FIELD RENDERER --------------------

  // Date Range Handlers
  const handleDateRangeChange = (item, ruleValue) => {
    setFormValues((prev) => ({
      ...prev,
      selectedRuleConditions: {
        ...prev.selectedRuleConditions,
        [ruleValue]: {
          from: item.selection.startDate,
          to: item.selection.endDate,
        },
      },
    }));

    setDateRangeStates((prev) => ({
      ...prev,
      [ruleValue]: [item.selection],
    }));
  };

  const clearDateRange = (ruleValue) => {
    setFormValues((prev) => ({
      ...prev,
      selectedRuleConditions: (() => {
        const newConditions = { ...prev.selectedRuleConditions };
        delete newConditions[ruleValue];
        return newConditions;
      })(),
    }));
    setDateRangeStates((prev) => {
      const newStates = { ...prev };
      delete newStates[ruleValue];
      return newStates;
    });
    setActiveDateRanges((prev) => {
      const newStates = { ...prev };
      delete newStates[ruleValue];
      return newStates;
    });
  };
  // -------------------- Date Range Handlers --------------------

  // Preview helper functions
  const renderPreviewDescription = (rule, conditionValue) => {
    if (!conditionValue) {
      return (
        <span className="ris-preview-placeholder">
          Members with {rule.label.toLowerCase()} (not set)
        </span>
      );
    }

    switch (rule.inputType) {
      case "select":
        const selectValue = conditionValue.label || conditionValue.value;
        switch (rule.value) {
          case "gym_id":
            return `Members from ${selectValue}`;
          case "trainer_id":
            return `Members whose trainer is ${selectValue}`;
          case "added_by":
            return `All subscriptions added by ${selectValue}`;
          case "payment_location":
            return `Members who paid at ${selectValue}`;
          case "bca_status":
            return `Members with BCA status: ${selectValue}`;
          case "biomax_register_status":
            return `Members with Biomax registration status: ${selectValue}`;
          case "tme_status":
            return `Members with TME status: ${selectValue}`;
          case "membership_type":
            return `Members with ${selectValue} membership`;
          case "subscription":
            return `Members with ${selectValue} subscription`;
          case "regular_plan_status":
            return `Members with regular plan status: ${selectValue}`;
          case "pt_plan_status":
            return `Members with PT plan status: ${selectValue}`;
          case "recurring":
            return `Members with recurring status: ${selectValue}`;
          case "payment_mode":
            return `Members who paid via ${selectValue}`;
          case "expiring_in":
            return `Members expiring in ${selectValue}`;
          case "member_checkin_day":
            return `Members who last checked in ${selectValue}`;
          case "trainer_checkin_day":
            return `Members whose trainer last checked in ${selectValue}`;
          case "three_day_pt_trial_status":
            return `Members with 3-day PT trial status: ${selectValue}`;
          case "three_day_pt_trial_session_status":
            return `Members with 3-day PT trial session status: ${selectValue}`;
          case "fitness_profile_status":
            return `Members with fitness profile status: ${selectValue}`;
          case "added_to_lms":
            return `Members ${selectValue} to LMS`;
          default:
            return `Members with ${rule.label.toLowerCase()}: ${selectValue}`;
        }
      case "multi-select":
        if (Array.isArray(conditionValue) && conditionValue.length > 0) {
          const values = conditionValue
            .map((item) => item.label || item.value)
            .join(", ");
          switch (rule.value) {
            case "member_checkin_time":
              return `Members who check in during: ${values}`;
            case "trainer_checkin_time":
              return `Members whose trainers check in during: ${values}`;
            default:
              return `Members with ${rule.label.toLowerCase()}: ${values}`;
          }
        }
        return (
          <span className="ris-preview-placeholder">
            Members with {rule.label.toLowerCase()} (not set)
          </span>
        );
      case "date-range":
        if (conditionValue.from && conditionValue.to) {
          const fromDate = new Date(conditionValue.from).toLocaleDateString();
          const toDate = new Date(conditionValue.to).toLocaleDateString();
          switch (rule.value) {
            case "member_checkin_date":
              return `Members who checked in between ${fromDate} and ${toDate}`;
            case "trainer_checkin_date":
              return `Members whose trainers checked in between ${fromDate} and ${toDate}`;
            case "expiring_in_date":
              return `Members expiring between ${fromDate} and ${toDate}`;
            case "subscription_add_date":
              return `Members whose subscription was added between ${fromDate} and ${toDate}`;
            default:
              return `Members with ${rule.label.toLowerCase()} between ${fromDate} and ${toDate}`;
          }
        }
        return (
          <span className="ris-preview-placeholder">
            Members with {rule.label.toLowerCase()} (not set)
          </span>
        );
      case "text":
        switch (rule.value) {
          case "added_by":
            return `All subscriptions added by ${conditionValue}`;
          default:
            return `Members with ${rule.label.toLowerCase()}: ${conditionValue}`;
        }
      case "number":
        switch (rule.value) {
          case "outstanding_amount":
            return `Members with outstanding amount > ${conditionValue}`;
          case "cart_value":
            return `Members with cart value > ${conditionValue}`;
          case "abandoned_since_hours":
            return `Members who abandoned cart ${conditionValue} hours ago`;
          default:
            return `Members with ${rule.label.toLowerCase()}: ${conditionValue}`;
        }
      case "date":
        switch (rule.value) {
          case "last_payment_date":
            return `Members who last paid on ${conditionValue}`;
          default:
            return `Members with ${rule.label.toLowerCase()}: ${conditionValue}`;
        }
      case "time":
        return `Members with ${rule.label.toLowerCase()}: ${conditionValue}`;
      default:
        return (
          <span className="ris-preview-placeholder">
            Members with {rule.label.toLowerCase()} (not set)
          </span>
        );
    }
  };

  const getEstimatedMemberCount = () => {
    // This is a mock estimation - in real implementation, you might want to call an API
    const baseCount = 1000;
    const ruleCount = formValues.selectedRules.length;
    const segmentType = formValues.selectedSegmentType?.label;

    let multiplier = 1;

    // Adjust based on segment type
    switch (segmentType) {
      case "Renewal":
        multiplier = 0.3; // ~30% of members are up for renewal
        break;
      case "Unpaid":
        multiplier = 0.1; // ~10% of members have unpaid dues
        break;
      case "Cart Abandoned":
        multiplier = 0.05; // ~5% abandon carts
        break;
      default:
        multiplier = 0.2;
    }

    // Adjust based on number of rules (more rules = more specific = fewer members)
    const ruleMultiplier = Math.max(0.1, 1 - (ruleCount - 1) * 0.2);

    const estimated = Math.round(baseCount * multiplier * ruleMultiplier);
    return `${estimated.toLocaleString()}+ members`;
  };

  const handleSubmitForm = async (payload) => {
    const method = isEdit ? "PUT" : "POST";
    const endpoint = "/campaignSegment";
    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        setIsSubmit(false);
        setFormValues(iniFormValues);
        callFunction();
        closeModal();
        toast.success(result?.payload?.message || "Success!");
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormErrors(validateSegmentForm(formValues));
    setIsSubmit(true);
  };

  useEffect(() => {
    const isGymRuleSelected = formValues.selectedRules.some(
      (rule) => rule.value === "gym_id"
    );

    if (isGymRuleSelected && userInfoData?.token) {
      dispatch(fetchGymData(userInfoData.token, 1, 100, "", "all"));
    }
  }, [formValues.selectedRuleConditions]);

  useEffect(() => {
    const isTrainerRuleSelected = formValues.selectedRules.some(
      (rule) => rule.value === "trainer_id"
    );

    if (isTrainerRuleSelected && userInfoData?.token) {
      dispatch(fetchTrainersData(userInfoData.token));
    }
  }, [formValues.selectedRuleConditions]);

  useEffect(() => {
    const isAddedByRuleSelected = formValues.selectedRules.some(
      (rule) => rule.value === "added_by"
    );

    if (isAddedByRuleSelected && userInfoData?.token) {
      dispatch(
        fetchInHouseUsersData({
          token: userInfoData?.token || "",
          qps: "user_types=admin,trainer,receptionist",
          pageNo: 1,
          limit: 100,
        })
      );
    }
  }, [formValues.selectedRuleConditions]);

  useEffect(() => {
    if (isSubmit && Object.keys(formErrors)?.length === 0) {
      // Helper function to format date
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      };

      // Helper function to extract numeric value from dropdown
      const extractNumericValue = (value) => {
        if (typeof value === 'string') {
          const num = parseInt(value);
          return isNaN(num) ? null : num;
        }
        return value;
      };

      // Helper function to extract gym IDs
      const extractGymIds = (gymData) => {
        if (!gymData) return [];
        if (Array.isArray(gymData)) {
          return gymData.map(gym => gym.value || gym.id || gym);
        }
        return [gymData.value || gymData.id || gymData];
      };

      // Build rules object based on selected rules
      const rules = {};
      
      formValues.selectedRules.forEach((rule) => {
        const conditionValue = formValues.selectedRuleConditions[rule.value];
        
        switch (rule.value) {
          case "expiring_in":
            // Map to subscription_expiring_in_days_from and subscription_expiring_in_days_to
            const expiringDays = extractNumericValue(conditionValue?.value || conditionValue);
            if (expiringDays !== null) {
              rules.subscription_expiring_in_days_from = expiringDays;
              rules.subscription_expiring_in_days_to = expiringDays;
            }
            break;
            
          case "expiring_in_date":
            // Map to subscription_expiring_before_date
            if (conditionValue?.to) {
              rules.subscription_expiring_before_date = formatDate(conditionValue.to);
            }
            break;
            
          case "member_checkin_day":
            // Map to lastcheckin_days_from and lastcheckin_days_to
            const checkinDays = extractNumericValue(conditionValue?.value || conditionValue);
            if (checkinDays !== null) {
              rules.lastcheckin_days_from = checkinDays;
              rules.lastcheckin_days_to = checkinDays;
            }
            break;
            
          case "member_checkin_date":
            // Map to lastcheckin_date
            if (conditionValue?.from) {
              rules.lastcheckin_date = formatDate(conditionValue.from);
            }
            break;
            
          case "gym_id":
            // Map to gym_ids
            const gymIds = extractGymIds(conditionValue);
            if (gymIds.length > 0) {
              rules.gym_ids = gymIds;
            }
            break;
            
          case "pt_plan_status":
            // Map to pt_status
            if (conditionValue?.value === "active" || conditionValue === "active") {
              rules.pt_status = true;
            } else if (conditionValue?.value === "inactive" || conditionValue === "inactive") {
              rules.pt_status = false;
            }
            break;
            
          case "pt_expiring_in":
            // Map to pt_expiring_in_days_from and pt_expiring_in_days_to
            const ptExpiringDays = extractNumericValue(conditionValue?.value || conditionValue);
            if (ptExpiringDays !== null) {
              rules.pt_expiring_in_days_from = ptExpiringDays;
              rules.pt_expiring_in_days_to = ptExpiringDays;
            }
            break;
            
          case "pt_expiring_date":
            // Map to pt_expiring_on_date
            if (conditionValue?.to) {
              rules.pt_expiring_on_date = formatDate(conditionValue.to);
            }
            break;
            
          case "age":
            // Map to age_from and age_to
            const age = extractNumericValue(conditionValue?.value || conditionValue);
            if (age !== null) {
              rules.age_from = age;
              rules.age_to = age;
            }
            break;
            
          case "gender":
            // Map to gender
            rules.gender = conditionValue?.value || conditionValue;
            break;
            
          case "trainer_id":
            // Map to trainer_id
            rules.trainer_id = conditionValue?.value || conditionValue;
            break;
            
          case "membership_type":
            // Map to membership_type
            rules.membership_type = conditionValue?.value || conditionValue;
            break;
            
          case "payment_mode":
            // Map to payment_mode
            rules.payment_mode = conditionValue?.value || conditionValue;
            break;
            
          case "biomax_register_status":
            // Map to biomax_status
            rules.biomax_status = conditionValue?.value === "true" || conditionValue === "true";
            break;
            
          case "fitness_profile_status":
            // Map to fitness_profile_status
            rules.fitness_profile_status = conditionValue?.value || conditionValue;
            break;
            
          case "added_to_lms":
            // Map to added_to_lms
            rules.added_to_lms = conditionValue?.value === "yes" || conditionValue === "yes";
            break;
            
          case "recurring":
            // Map to recurring_status
            rules.recurring_status = conditionValue?.value || conditionValue;
            break;
            
          case "regular_plan_status":
            // Map to regular_plan_status
            rules.regular_plan_status = conditionValue?.value || conditionValue;
            break;
            
          case "three_day_pt_trial_status":
            // Map to pt_trial_status
            rules.pt_trial_status = conditionValue?.value || conditionValue;
            break;
            
          case "three_day_pt_trial_session_status":
            // Map to pt_trial_session_status
            rules.pt_trial_session_status = conditionValue?.value || conditionValue;
            break;
            
          case "payment_location":
            // Map to payment_location
            rules.payment_location = conditionValue?.value || conditionValue;
            break;
            
          case "bca_status":
            // Map to bca_status
            rules.bca_status = conditionValue?.value || conditionValue;
            break;
            
          case "tme_status":
            // Map to tme_status
            rules.tme_status = conditionValue?.value || conditionValue;
            break;
            
          case "membership_status":
            // Map to membership_status
            rules.membership_status = conditionValue?.value || conditionValue;
            break;
            
          case "added_by":
            // Map to added_by
            rules.added_by = conditionValue?.value || conditionValue;
            break;
            
          case "member_checkin_time":
            // Map to checkin_time_slots
            if (Array.isArray(conditionValue) && conditionValue.length > 0) {
              rules.checkin_time_slots = conditionValue.map(item => item.value || item);
            }
            break;
            
          case "trainer_checkin_time":
            // Map to trainer_checkin_time_slots
            if (Array.isArray(conditionValue) && conditionValue.length > 0) {
              rules.trainer_checkin_time_slots = conditionValue.map(item => item.value || item);
            }
            break;
            
          case "trainer_checkin_day":
            // Map to trainer_checkin_days_from and trainer_checkin_days_to
            const trainerCheckinDays = extractNumericValue(conditionValue?.value || conditionValue);
            if (trainerCheckinDays !== null) {
              rules.trainer_checkin_days_from = trainerCheckinDays;
              rules.trainer_checkin_days_to = trainerCheckinDays;
            }
            break;
            
          case "trainer_checkin_date":
            // Map to trainer_checkin_date
            if (conditionValue?.from) {
              rules.trainer_checkin_date = formatDate(conditionValue.from);
            }
            break;
            
          case "subscription_add_date":
            // Map to subscription_added_date_from and subscription_added_date_to
            if (conditionValue?.from) {
              rules.subscription_added_date_from = formatDate(conditionValue.from);
            }
            if (conditionValue?.to) {
              rules.subscription_added_date_to = formatDate(conditionValue.to);
            }
            break;
            
          case "expired_days_ago":
            // Map to expired_days_ago
            const expiredDays = extractNumericValue(conditionValue?.value || conditionValue);
            if (expiredDays !== null) {
              rules.expired_days_ago = expiredDays;
            }
            break;
            
          case "outstanding_amount":
            // Map to outstanding_amount
            const outstandingAmount = extractNumericValue(conditionValue);
            if (outstandingAmount !== null) {
              rules.outstanding_amount = outstandingAmount;
            }
            break;
            
          case "cart_value":
            // Map to cart_value
            const cartValue = extractNumericValue(conditionValue);
            if (cartValue !== null) {
              rules.cart_value = cartValue;
            }
            break;
            
          case "abandoned_since_hours":
            // Map to abandoned_since_hours
            const abandonedHours = extractNumericValue(conditionValue);
            if (abandonedHours !== null) {
              rules.abandoned_since_hours = abandonedHours;
            }
            break;
            
          case "last_payment_date":
            // Map to last_payment_date
            rules.last_payment_date = formatDate(conditionValue);
            break;
            
          case "date_added":
            // Map to cart_abandoned_date_from and cart_abandoned_date_to
            if (conditionValue?.from) {
              rules.cart_abandoned_date_from = formatDate(conditionValue.from);
            }
            if (conditionValue?.to) {
              rules.cart_abandoned_date_to = formatDate(conditionValue.to);
            }
            break;
            
          default:
            // For any other rules, map them directly
            if (conditionValue) {
              if (Array.isArray(conditionValue)) {
                rules[rule.value] = conditionValue.map(item => item.value || item);
              } else if (typeof conditionValue === 'object' && conditionValue.value !== undefined) {
                rules[rule.value] = conditionValue.value;
              } else {
                rules[rule.value] = conditionValue;
              }
            }
            break;
        }
      });

      const userData = {
        name: formValues.segmentName,
        description: formValues.description,
        valid_from: formatDate(new Date()), // Set to current date
        valid_to: formatDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)), // Set to 1 year from now
        user_type: "member",
        member_type: formValues.selectedSegmentType?.value?.toLowerCase() || "renewal",
        rules: rules
      };

      const handleConfirm = async () => {
        handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure you want to submit this segment?`,
        "Edit Form",
        "Submit Form"
      );
    }
  }, [isSubmit, formErrors]);

  console.log("formValues", formValues.selectedRuleConditions["added_by"]);

  return (
    <div className="createNewCTOModalContainer mod-width-50p mod-height-95vh ris-container">
      <CommonHeadingNBtns
        title="Create Segment"
        onClose={closeModal}
        btnTxt="Submit"
        onClick={handleSubmit}
        isLoading={postPutLoading}
      />

      <form className="ris-form mb30">
        {/* Segment Type & Metadata Card */}
        <div className="ris-card">
          <h2 className="ris-card-header">Segment Type & Metadata</h2>
          <div className="ris-form-group">
            <label className="ris-label">
              Segment Name <span className="required">*</span>
            </label>

            <input
              type="text"
              className={`ris-input ${
                formErrors.segmentName ? "formError" : ""
              }`}
              placeholder="Enter Segment Name"
              value={formValues.segmentName}
              onChange={onChangeInput}
              name="segmentName"
            />
          </div>

          <div className="ris-form-group">
            <label className="ris-label">
              Segment Type <span className="required">*</span>
            </label>

            <div
              className={`ris-select ${
                formErrors.selectedSegmentType ? "formError" : ""
              }`}
            >
              <SelectDropdown
                options={segment_types}
                selectedOption={formValues.selectedSegmentType}
                setSelectedOption={(selectedValue) =>
                  onChangeDropdown(selectedValue, "selectedSegmentType")
                }
                name="Segment Type"
              />
            </div>
          </div>

          <div className="ris-form-group">
            <label className="ris-label">
              Description <span className="required">*</span>
            </label>

            <textarea
              className={`ris-input ris-textarea ${
                formErrors.description ? "formError" : ""
              }`}
              placeholder="Enter Description"
              value={formValues.description}
              onChange={(e) => onChangeInput(e)}
              name="description"
            ></textarea>
          </div>
        </div>

        {/* Collapsible Rules Accordion */}
        <div className="ris-rules-accordion">
          <div
            className={`ris-accordion-header ${
              formErrors.selectedRules ? "formError" : ""
            }`}
            onClick={() => setIsRulesExpanded(!isRulesExpanded)}
          >
            <div className="ris-accordion-title">
              Select Rules for <b>{formValues.selectedSegmentType?.label}</b>
              {formValues.selectedRules.length > 0 && (
                <span className="ris-accordion-count">
                  {formValues.selectedRules.length} selected
                </span>
              )}
            </div>
            <div
              className={`ris-accordion-toggle ${
                isRulesExpanded ? "expanded" : ""
              }`}
            >
              ▼
            </div>
          </div>

          <div
            className={`ris-accordion-content ${
              isRulesExpanded ? "expanded" : ""
            }`}
          >
            <div className="ris-rule-chips-container">
              {availableRules.length > 0 && (
                <div
                  className={`ris-rule-chip ${
                    areAllRulesSelected ? "selected" : ""
                  }`}
                  onClick={handleSelectAllRules}
                >
                  <span className="checkmark">
                    {areAllRulesSelected ? "✔" : "+"}
                  </span>
                  {areAllRulesSelected ? "Deselect All" : "Select All"}
                </div>
              )}
              {availableRules.map((rule) => (
                <div
                  key={rule.value}
                  className={`ris-rule-chip ${
                    formValues.selectedRules.some((r) => r.value === rule.value)
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleRuleToggle(rule.value)}
                >
                  <span className="checkmark">
                    {formValues.selectedRules.some(
                      (r) => r.value === rule.value
                    )
                      ? "✔"
                      : "+"}
                  </span>
                  {rule.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {formValues.selectedRules.length > 0 && (
          <div className="ris-dynamic-conditions-grid mb30">
            {formValues.selectedRules.map((rule) => (
              <div
                className={`ris-condition-card ${
                  formErrors[`rule_${rule.value}`] ? "formError" : ""
                }`}
                key={rule.value}
              >
                <button
                  type="button"
                  className="ris-condition-remove-btn"
                  title="Remove"
                  onClick={() => handleRuleToggle(rule.value)}
                >
                  ×
                </button>
                <label className="ris-condition-card-label">{rule.label}</label>
                {renderFieldForRule(rule)}

                {/* {isSubmit && formErrors[`rule_${rule.value}`] && (
                  <div className="ris-error-message">
                    {formErrors[`rule_${rule.value}`]}
                  </div>
                )} */}
              </div>
            ))}
            {/* Render the popup only once, outside the cards */}
            {Object.entries(activeDateRanges).map(([ruleValue, isActive]) =>
              isActive ? (
                <div className="ris-date-ranger-popup" key={ruleValue}>
                  <DateRanger
                    handleDateRangeChange={(item) =>
                      handleDateRangeChange(item, ruleValue)
                    }
                    rangeState={
                      dateRangeStates[ruleValue] || [
                        {
                          startDate: new Date(),
                          endDate: new Date(),
                          key: "selection",
                        },
                      ]
                    }
                    closeRanger={() =>
                      setActiveDateRanges((prev) => ({
                        ...prev,
                        [ruleValue]: false,
                      }))
                    }
                    onClose={() =>
                      setActiveDateRanges((prev) => ({
                        ...prev,
                        [ruleValue]: false,
                      }))
                    }
                    onClear={() => clearDateRange(ruleValue)}
                  />
                </div>
              ) : null
            )}
          </div>
        )}

        <div className="formInputBox checkbox-wrapper span3">
          <div className={`flex-box`}>
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="isActive"
                checked={formValues.isActive}
                onChange={onChangeInput}
              />
              Active
            </label>
          </div>
        </div>
      </form>

      {/* Preview Section */}
      {formValues.selectedSegmentType &&
        formValues.selectedRules.length > 0 && (
          <div className="ris-preview-section">
            <div className="ris-preview-header">
              <h3>Segment Preview</h3>
              <div className="ris-preview-badge">
                {formValues.selectedRules.length} rule
                {formValues.selectedRules.length > 1 ? "s" : ""} selected
              </div>
            </div>

            <div className="ris-preview-content">
              <div className="ris-preview-summary">
                <h4>This segment will include:</h4>
                <div className="ris-preview-criteria">
                  {formValues.selectedRules.map((rule) => {
                    const conditionValue =
                      formValues.selectedRuleConditions[rule.value];
                    return (
                      <div key={rule.value} className="ris-preview-criterion">
                        <span className="ris-preview-rule">
                          • {renderPreviewDescription(rule, conditionValue)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="ris-preview-estimate">
                <div className="ris-preview-estimate-item">
                  <span className="ris-preview-estimate-label">
                    Segment Type:
                  </span>
                  <span className="ris-preview-estimate-value">
                    {formValues.selectedSegmentType.label}
                  </span>
                </div>
                <div className="ris-preview-estimate-item">
                  <span className="ris-preview-estimate-label">
                    Estimated Members:
                  </span>
                  <span className="ris-preview-estimate-value">
                    {getEstimatedMemberCount()}
                  </span>
                </div>
                <div className="ris-preview-estimate-item">
                  <span className="ris-preview-estimate-label">
                    Last Updated:
                  </span>
                  <span className="ris-preview-estimate-value">
                    {new Date().toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

      <CloseSubmitBtn
        closeLabel="Cancel"
        submitLabel="Activate Segment"
        onClose={closeModal}
        onSubmit={handleSubmit}
        isLoading={postPutLoading}
      />
    </div>
  );
};

export default SegementForm;
